// Column definitions for the accounts list table.
// Kept separate from AccountsListPage so the page stays focused on layout
// and state; new columns get added here without touching the page logic.

import type * as React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AccountManagerCell } from '../components/AccountManagerCell';
import type { AccountTableRow } from '../types';
import type { ColumnDef } from './accountsColumnDefs.types';

// ATEK HubSpot portal ID — same constant used throughout CRM pages
// (e.g. ApolloPage, CrmContactDetailPage). Keep in sync if it ever changes.
const HUBSPOT_PORTAL_ID = '22484617';

export type AccountColumnDef = ColumnDef<AccountTableRow>;

const statusStyles: Record<string, string> = {
  active: 'bg-green-100 text-green-800 border-green-200',
  prospect: 'bg-blue-100 text-blue-800 border-blue-200',
  onboarding: 'bg-purple-100 text-purple-800 border-purple-200',
  suspended: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  churned: 'bg-red-100 text-red-800 border-red-200',
};

function formatCurrency(value: number | null, currency = 'CAD'): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

/**
 * Render a manager-name cell as a link to the contact detail page. When the
 * role has multiple contacts, only the primary one (ordinal=0) is linkable —
 * the full name string is shown, click opens the primary. Falls back to plain
 * text when the contact id is missing (e.g. role assigned via legacy path).
 */
function contactLink(contactId: string | null, name: string | null): React.ReactNode {
  if (!name) return <span className="text-muted-foreground">—</span>;
  if (!contactId) return name;
  return (
    <Link
      to={`/crm/contacts/${contactId}`}
      onClick={(e) => e.stopPropagation()}
      className="hover:underline text-atek-blue"
    >
      {name}
    </Link>
  );
}

/**
 * Render a numeric value as a clickable link to the account's detail page,
 * scoped to the given tab + optional filter params. `e.stopPropagation()`
 * keeps the row-level navigation from firing in parallel.
 */
function tabLink(
  accountId: string,
  tab: string,
  extra: Record<string, string> | undefined,
  className: string,
  content: React.ReactNode,
) {
  const params = new URLSearchParams({ tab, ...(extra || {}) });
  return (
    <Link
      to={`/accounts/${accountId}?${params.toString()}`}
      onClick={(e) => e.stopPropagation()}
      className={cn('hover:underline', className)}
    >
      {content}
    </Link>
  );
}

// Contractual Mgr and Sales Mgr (Account Mgr) audit dots were removed —
// those name columns already exist as text columns and surface the
// missing-assignment signal directly (empty cell = nobody assigned).
// All remaining audit columns (Drive / HubSpot / QB) render real links
// when present, so there are no "plain" dot columns left.

// QB: clickable customer link when present, red dot when missing.
const QB_COLUMN: AccountColumnDef = {
  key: 'has_qb_customer_id',
  label: 'QB',
  filterType: 'select',
  filterOptions: [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ],
  getValue: (a) => (a.has_qb_customer_id ? 'true' : 'false'),
  render: (a) =>
    a.has_qb_customer_id && a.quickbooks_customer_id ? (
      <a
        href={`https://qbo.intuit.com/app/customerdetail?nameId=${encodeURIComponent(a.quickbooks_customer_id)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center text-green-700 hover:text-green-900"
        title="Open QuickBooks customer"
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    ) : (
      <span
        className="inline-block h-2.5 w-2.5 rounded-full bg-red-400"
        title="QB: No"
      />
    ),
  align: 'center',
  defaultVisible: false,
};

// Drive: clickable folder link when present, red dot when missing.
const DRIVE_COLUMN: AccountColumnDef = {
  key: 'has_drive_folder',
  label: 'Drive',
  filterType: 'select',
  filterOptions: [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ],
  getValue: (a) => (a.has_drive_folder ? 'true' : 'false'),
  render: (a) =>
    a.has_drive_folder && a.gdrive_root_folder_id ? (
      <a
        href={`https://drive.google.com/drive/folders/${encodeURIComponent(a.gdrive_root_folder_id)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center text-blue-600 hover:text-blue-800"
        title="Open Drive folder"
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    ) : (
      <span
        className="inline-block h-2.5 w-2.5 rounded-full bg-red-400"
        title="Drive: No"
      />
    ),
  align: 'center',
  defaultVisible: false,
};

// HubSpot: clickable company link when present, red dot when missing.
const HUBSPOT_COLUMN: AccountColumnDef = {
  key: 'has_hubspot_company_id',
  label: 'HubSpot',
  filterType: 'select',
  filterOptions: [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ],
  getValue: (a) => (a.has_hubspot_company_id ? 'true' : 'false'),
  render: (a) =>
    a.has_hubspot_company_id && a.hubspot_company_id ? (
      <a
        href={`https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/company/${a.hubspot_company_id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center text-orange-600 hover:text-orange-800"
        title="Open HubSpot company"
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    ) : (
      <span
        className="inline-block h-2.5 w-2.5 rounded-full bg-red-400"
        title="HubSpot: No"
      />
    ),
  align: 'center',
  defaultVisible: false,
};

const AUDIT_COLUMNS: AccountColumnDef[] = [DRIVE_COLUMN, HUBSPOT_COLUMN, QB_COLUMN];

export const COLUMNS: AccountColumnDef[] = [
  {
    key: 'account_number',
    label: 'Account #',
    sortKey: 'account_number',
    filterType: 'text',
    getValue: (a) => a.account_number,
    render: (a) => (
      <div className="inline-flex items-center gap-1.5">
        <span className="font-mono text-xs">{a.account_number}</span>
        <Link
          to={`/accounts/${a.id}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open account ${a.account_number} in a new tab`}
          className="text-muted-foreground transition-colors hover:text-foreground"
          onClick={(event) => event.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>
    ),
  },
  {
    key: 'name',
    label: 'Account Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (a) => a.name,
    render: (a) => <span className="font-medium">{a.name}</span>,
  },
  {
    key: 'has_calibration_contract',
    label: 'Cal.',
    filterType: 'select',
    filterOptions: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
      { label: 'Other', value: 'other' },
    ],
    getValue: (a) => a.has_calibration_contract || 'false',
    render: (a) => {
      const v = a.has_calibration_contract;
      if (v === 'true')
        return (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-green-500"
            title="Calibration: Yes"
          />
        );
      if (v === 'other')
        return (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400"
            title="Calibration: Other"
          />
        );
      return <span className="text-muted-foreground">—</span>;
    },
    align: 'center',
  },
  {
    key: 'requires_whitelisting',
    label: 'WL',
    filterType: 'select',
    filterOptions: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
      { label: 'Other', value: 'other' },
    ],
    getValue: (a) => a.requires_whitelisting || 'false',
    render: (a) => {
      const v = a.requires_whitelisting;
      if (v === 'true')
        return (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-green-500"
            title="Whitelisting: Yes"
          />
        );
      if (v === 'other')
        return (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400"
            title="Whitelisting: Other"
          />
        );
      return <span className="text-muted-foreground">—</span>;
    },
    align: 'center',
    defaultVisible: false,
  },
  {
    key: 'calibrations_due',
    label: 'Cal. Due',
    sortKey: 'calibrations_due',
    filterType: 'number-sum',
    getValue: (a) => a.calibrations_due,
    render: (a) =>
      a.calibrations_due > 0
        ? tabLink(
            a.id,
            'sensors',
            { calibration: 'due-soon' },
            'inline-flex items-center gap-1 text-amber-600 font-medium',
            (
              <>
                <AlertTriangle className="h-3.5 w-3.5" />
                {a.calibrations_due}
              </>
            ),
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
  },
  {
    key: 'overdue_calibrations',
    label: 'Overdue Cal.',
    sortKey: 'overdue_calibrations',
    filterType: 'number-sum',
    getValue: (a) => a.overdue_calibrations,
    render: (a) =>
      a.overdue_calibrations > 0
        ? tabLink(
            a.id,
            'sensors',
            { calibration: 'overdue' },
            'inline-flex items-center gap-1 font-medium text-red-600',
            (
              <>
                <AlertTriangle className="h-3.5 w-3.5" />
                {a.overdue_calibrations}
              </>
            ),
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
  },
  {
    key: 'contractual_manager_name',
    label: 'Contractual Mgr',
    sortKey: 'contractual_manager_name',
    filterType: 'text',
    getValue: (a) => a.contractual_manager_name || '',
    render: (a) => contactLink(a.contractual_manager_contact_id, a.contractual_manager_name),
  },
  {
    key: 'technical_manager_name',
    label: 'Technical Mgr',
    filterType: 'text',
    getValue: (a) => a.technical_manager_name || '',
    render: (a) => contactLink(a.technical_manager_contact_id, a.technical_manager_name),
  },
  {
    key: 'it_manager_name',
    label: 'IT Mgr',
    filterType: 'text',
    getValue: (a) => a.it_manager_name || '',
    render: (a) => contactLink(a.it_manager_contact_id, a.it_manager_name),
  },
  {
    key: 'sales_manager_name',
    label: 'Account Mgr',
    filterType: 'single-select',
    getValue: (a) => a.sales_manager_name || '',
    getFilterValue: (a) => a.sales_manager_id || '',
    render: (a) => <AccountManagerCell account={a} />,
  },
  {
    key: 'contract_number',
    label: 'Subscription #',
    sortKey: 'contract_number',
    filterType: 'text',
    getValue: (a) => a.contract_number || '',
    render: (a) =>
      a.contract_number ? (
        <span className="font-mono text-xs">{a.contract_number}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    filterType: 'select',
    filterOptions: [
      { label: 'Active', value: 'active' },
      { label: 'Prospect', value: 'prospect' },
      { label: 'Onboarding', value: 'onboarding' },
      { label: 'Suspended', value: 'suspended' },
      { label: 'Churned', value: 'churned' },
    ],
    getValue: (a) => a.status,
    render: (a) => (
      <span
        className={cn(
          'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize',
          statusStyles[a.status] || 'bg-gray-100 text-gray-800 border-gray-200',
        )}
      >
        {a.status}
      </span>
    ),
  },
  {
    key: 'sensors_qty',
    label: 'Sensors Total',
    // sortKey must match the rendered value. `sensors_qty` is the legacy
    // active-only count (status=1) and would make the header sort by a
    // different number than the cell shows. `total_sensors_count` matches
    // sc_live.total_sensors (every non-deleted sensor).
    sortKey: 'total_sensors_count',
    filterType: 'number-sum',
    // True total of non-deleted sensors regardless of status — comes straight
    // from account_health_metrics.total_sensors_count. Don't substitute
    // `active + inactive` here: with the strict-inactive definition (status=5
    // only), that sum excludes archived/maintenance/inhibited/etc.
    getValue: (a) => a.total_sensors_count,
    render: (a) =>
      a.total_sensors_count > 0
        ? tabLink(a.id, 'sensors', undefined, '', a.total_sensors_count)
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
  },
  {
    key: 'contract_start_date',
    label: 'Subscription Start',
    sortKey: 'contract_start_date',
    filterType: 'date-range',
    getValue: (a) => a.contract_start_date || '',
    render: (a) =>
      a.contract_start_date ? (
        formatDate(a.contract_start_date)
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'contract_years',
    label: 'Years',
    filterType: 'number-range',
    getValue: (a) => a.contract_years,
    render: (a) =>
      a.contract_years != null ? (
        `${a.contract_years}y`
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    align: 'right',
    defaultVisible: false,
  },
  {
    key: 'contract_end_date',
    label: 'Subscription End',
    sortKey: 'contract_end_date',
    filterType: 'date-range',
    getValue: (a) => a.contract_end_date || '',
    render: (a) =>
      a.contract_end_date ? (
        formatDate(a.contract_end_date)
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'arr',
    label: 'ARR',
    sortKey: 'arr',
    filterType: 'number-sum',
    sumFormat: 'currency',
    getValue: (a) => a.arr,
    render: (a) =>
      a.arr != null && a.arr > 0 ? (
        <span className="text-emerald-600 font-medium">
          {formatCurrency(a.arr, a.currency)}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    align: 'right',
    className: 'tabular-nums',
  },
  {
    key: 'ltv',
    label: 'LTV',
    sortKey: 'ltv',
    filterType: 'number-sum',
    sumFormat: 'currency',
    getValue: (a) => a.ltv,
    render: (a) =>
      a.ltv > 0 ? (
        <span className="text-emerald-600 font-medium">
          {formatCurrency(a.ltv, a.currency)}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    align: 'right',
    className: 'tabular-nums',
  },
  // -------------------------------------------------------------------------
  // Operational columns (hidden by default — surfaced via Operations preset).
  // Source: account_health_metrics latest snapshot, joined server-side in
  // AccountsDAO.getManyEnriched. Zero values are the "needs attention" signal:
  // an account with 0 active TX or 0 invoices is almost certainly misconfigured.
  // -------------------------------------------------------------------------
  {
    key: 'active_transmitters_count',
    label: 'Transmitter Active',
    sortKey: 'active_transmitters_count',
    filterType: 'number-sum',
    getValue: (a) => a.active_transmitters_count,
    render: (a) =>
      a.active_transmitters_count > 0
        ? tabLink(
            a.id,
            'transmitters',
            { status: 'active' },
            'font-medium text-emerald-600',
            a.active_transmitters_count,
          )
        : <span className="text-rose-600 font-medium">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'inactive_transmitters_count',
    label: 'Transmitter Inactive',
    sortKey: 'inactive_transmitters_count',
    filterType: 'number-sum',
    getValue: (a) => a.inactive_transmitters_count,
    render: (a) =>
      a.inactive_transmitters_count > 0
        ? tabLink(
            a.id,
            'transmitters',
            { status: 'inactive' },
            'font-medium text-rose-600',
            a.inactive_transmitters_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'gateways_count',
    label: 'Gateways',
    sortKey: 'gateways_count',
    filterType: 'number-sum',
    getValue: (a) => a.gateways_count,
    render: (a) =>
      a.gateways_count > 0
        ? tabLink(
            a.id,
            'transmitters',
            { type: 'gateways' },
            'font-medium text-foreground',
            a.gateways_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'sim_cards_count',
    label: 'SIM Cards',
    sortKey: 'sim_cards_count',
    filterType: 'number-sum',
    getValue: (a) => a.sim_cards_count,
    render: (a) =>
      a.sim_cards_count > 0
        ? <span className="font-medium text-foreground">{a.sim_cards_count}</span>
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'usergroups_count',
    label: 'Usergroups',
    sortKey: 'usergroups_count',
    filterType: 'number-sum',
    getValue: (a) => a.usergroups_count,
    render: (a) =>
      a.usergroups_count > 0
        ? <span className="font-medium text-foreground">{a.usergroups_count}</span>
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: true,
  },
  {
    key: 'users_count',
    label: 'Users',
    sortKey: 'users_count',
    filterType: 'number-sum',
    getValue: (a) => a.users_count,
    render: (a) =>
      a.users_count > 0
        ? <span className="font-medium text-foreground">{a.users_count}</span>
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: true,
  },
  {
    key: 'active_sensors_count',
    label: 'Sensor Active',
    sortKey: 'active_sensors_count',
    filterType: 'number-sum',
    getValue: (a) => a.active_sensors_count,
    render: (a) =>
      a.active_sensors_count > 0
        ? tabLink(
            a.id,
            'sensors',
            { status: 'active' },
            'font-medium text-emerald-600',
            a.active_sensors_count,
          )
        : <span className="text-rose-600 font-medium">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'inactive_sensors_count',
    label: 'Sensor Inactive',
    sortKey: 'inactive_sensors_count',
    filterType: 'number-sum',
    getValue: (a) => a.inactive_sensors_count,
    render: (a) =>
      a.inactive_sensors_count > 0
        ? tabLink(
            a.id,
            'sensors',
            { status: 'inactive' },
            'font-medium text-rose-600',
            a.inactive_sensors_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'maintenance_sensors_count',
    label: 'Sensors in Maintenance',
    sortKey: 'maintenance_sensors_count',
    filterType: 'number-sum',
    getValue: (a) => a.maintenance_sensors_count,
    render: (a) =>
      a.maintenance_sensors_count > 0
        ? tabLink(
            a.id,
            'sensors',
            { status: 'maintenance' },
            'font-medium text-amber-600',
            a.maintenance_sensors_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'in_alarm_sensors_count',
    label: 'Sensors In-Alarm',
    sortKey: 'in_alarm_sensors_count',
    filterType: 'number-sum',
    getValue: (a) => a.in_alarm_sensors_count,
    render: (a) =>
      a.in_alarm_sensors_count > 0
        ? tabLink(
            a.id,
            'sensors',
            { status: 'in-alarm' },
            'font-medium text-rose-600',
            a.in_alarm_sensors_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'unmanaged_sensors_count',
    label: 'Sensors Unmanaged',
    sortKey: 'unmanaged_sensors_count',
    filterType: 'number-sum',
    getValue: (a) => a.unmanaged_sensors_count,
    render: (a) =>
      a.unmanaged_sensors_count > 0
        ? tabLink(
            a.id,
            'sensors',
            { status: 'unmanaged' },
            'font-medium text-slate-500',
            a.unmanaged_sensors_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'inhibited_sensors_count',
    label: 'Sensors Inhibited',
    sortKey: 'inhibited_sensors_count',
    filterType: 'number-sum',
    getValue: (a) => a.inhibited_sensors_count,
    render: (a) =>
      a.inhibited_sensors_count > 0
        ? tabLink(
            a.id,
            'sensors',
            { status: 'inhibited' },
            'font-medium text-slate-500',
            a.inhibited_sensors_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'open_tickets_count',
    label: 'Open Tickets',
    sortKey: 'open_tickets_count',
    filterType: 'number-sum',
    getValue: (a) => a.open_tickets_count,
    render: (a) =>
      a.open_tickets_count > 0
        ? tabLink(
            a.id,
            'tickets',
            { status: 'open' },
            'font-medium text-violet-600',
            a.open_tickets_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'alert_24h_count',
    label: 'Alerts 24h',
    filterType: 'number-sum',
    getValue: (a) => a.alert_24h_count,
    render: (a) =>
      a.alert_24h_count > 0 ? (
        <span className="font-medium text-amber-600">{a.alert_24h_count}</span>
      ) : (
        <span className="text-muted-foreground">0</span>
      ),
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'emails_24h_count',
    label: 'Emails 24h',
    filterType: 'number-sum',
    getValue: (a) => a.emails_24h_count,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'calls_24h_count',
    label: 'Calls 24h',
    filterType: 'number-sum',
    getValue: (a) => a.calls_24h_count,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'low_battery_devices_count',
    label: 'Low Battery',
    filterType: 'number-sum',
    getValue: (a) => a.low_battery_devices_count,
    render: (a) =>
      a.low_battery_devices_count > 0
        ? tabLink(
            a.id,
            'transmitters',
            { battery: 'low' },
            'font-medium text-amber-600',
            a.low_battery_devices_count,
          )
        : <span className="text-muted-foreground">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'days_until_subscription_expiry',
    label: 'Sub Expires (d)',
    filterType: 'number-range',
    getValue: (a) => a.days_until_subscription_expiry,
    render: (a) => {
      const d = a.days_until_subscription_expiry;
      if (d === null || d === undefined) {
        return <span className="text-muted-foreground">—</span>;
      }
      if (d < 0) return <span className="font-medium text-red-600">{d}</span>;
      if (d <= 90) return <span className="font-medium text-amber-600">{d}</span>;
      return <span className="text-muted-foreground">{d}</span>;
    },
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  {
    key: 'total_invoices_count',
    label: 'Invoices',
    sortKey: 'total_invoices_count',
    filterType: 'number-sum',
    getValue: (a) => a.total_invoices_count,
    render: (a) =>
      a.total_invoices_count > 0
        ? tabLink(a.id, 'invoices', undefined, 'text-foreground', a.total_invoices_count)
        : <span className="text-rose-600 font-medium">0</span>,
    align: 'right',
    className: 'tabular-nums',
    defaultVisible: false,
  },
  // Audit / configuration-completeness dots (Audit preset surfaces these)
  ...AUDIT_COLUMNS,
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: () => <ChevronRight className="h-4 w-4 text-gray-400" />,
    align: 'right',
  },
];
